import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Link2, Send, Users2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ActivitiesShell from "../components/activities/ActivitiesShell";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AutoImageSlider from "../components/AutoImageSlider";

const MAX_SLOTS = 10;

// Convert Google Drive share link → direct embed URL
function normalizeDriveUrl(url) {
  url = (url || "").trim();
  if (!url) return "";
  const m = url.match(/drive\.google\.com\/file\/d\/([^/?#]+)/);
  if (m) return `https://drive.google.com/uc?export=view&id=${m[1]}`;
  const m2 = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
  if (m2) return `https://drive.google.com/uc?export=view&id=${m2[1]}`;
  return url;
}

function feedImageUrl(post) {
  if (post?.gallery_srcs?.length) return post.gallery_srcs[0];
  if (post?.image_src) return post.image_src;
  return post?.id ? `${API}/activities/${post.id}/image` : "";
}

export default function ActivitiesFeed() {
  const { member, loading: memberLoading } = useAuth();
  const isMember = Boolean(member && member !== false);

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  // Slots: { type: 'file'|'url', file: File|null, url: string, preview: string }
  const [slots, setSlots] = useState([]);
  const fileInputRef = useRef(null);

  const [failedImages, setFailedImages] = useState({});

  const refreshPosts = async () => {
    setLoadingPosts(true);
    try {
      const { data } = await api.get("/activities");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingPosts(false);
    }
  };

  useEffect(() => { refreshPosts(); }, []);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => { slots.forEach((s) => s.preview && URL.revokeObjectURL(s.preview)); };
  }, []); // eslint-disable-line

  // ── slot management ──────────────────────────────────────────────────────────
  const addFileSlots = (files) => {
    const remaining = MAX_SLOTS - slots.length;
    if (remaining <= 0) { toast.error(`Max ${MAX_SLOTS} images.`); return; }
    const toAdd = Array.from(files).slice(0, remaining);
    if (Array.from(files).length > remaining)
      toast.warning(`Only ${remaining} more slot${remaining === 1 ? "" : "s"} left. Added first ${remaining}.`);
    setSlots((prev) => [
      ...prev,
      ...toAdd.map((f) => ({ type: "file", file: f, url: "", preview: URL.createObjectURL(f) })),
    ]);
  };

  const addUrlSlot = () => {
    if (slots.length >= MAX_SLOTS) { toast.error(`Max ${MAX_SLOTS} images.`); return; }
    setSlots((prev) => [...prev, { type: "url", file: null, url: "", preview: "" }]);
  };

  const removeSlot = (idx) => {
    setSlots((prev) => {
      if (prev[idx]?.preview) URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const updateUrlSlot = (idx, val) => {
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, url: val } : s)));
  };

  const clearAll = () => {
    slots.forEach((s) => s.preview && URL.revokeObjectURL(s.preview));
    setSlots([]);
  };

  // ── submit ───────────────────────────────────────────────────────────────────
  const submitPost = async (e) => {
    e.preventDefault();
    if (!isMember) { toast.error("Sign in as a member to share a post."); return; }
    if (title.trim().length < 2 || body.trim().length < 2) {
      toast.error("Please add a short title and message."); return;
    }

    const validSlots = slots.filter(
      (s) => (s.type === "file" && s.file) || (s.type === "url" && s.url.trim())
    );

    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("body", body.trim());

    const fileSlots = validSlots.filter((s) => s.type === "file");
    const urlSlots = validSlots.filter((s) => s.type === "url");

    fileSlots.forEach(({ file }) => fd.append("images", file));
    if (urlSlots.length) {
      fd.append("image_urls", JSON.stringify(urlSlots.map((s) => normalizeDriveUrl(s.url.trim()))));
    }

    setSubmitting(true);
    try {
      await api.post("/member/activities", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Your post has been sent to admin for approval.");
      setTitle(""); setBody(""); clearAll();
      await refreshPosts();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActivitiesShell
      introTitle="A place to see our events, seva and togetherness."
      introSubtitle="Here we share the moments that matter to our forum, from learning sessions and family get-togethers to seva visits and community celebrations."
    >
      <div className="space-y-6">
        {/* ── Post compose box ── */}
        <div className="card-premium">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="overline mb-3">Share a post</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-light text-ink">
                Post something for the community
              </h2>
              <p className="mt-2 text-ink-soft leading-relaxed max-w-2xl">
                Share a short update and up to {MAX_SLOTS} photos — from your device or Google Drive links. Admin will approve before it appears here.
              </p>
            </div>
            <Users2 className="hidden md:block h-8 w-8 text-brand-secondary" />
          </div>

          {memberLoading ? (
            <div className="mt-6 rounded-2xl bg-cream-deep/60 p-4 text-sm text-ink-muted">
              Checking your member sign-in...
            </div>
          ) : isMember ? (
            <form className="mt-6 space-y-4" onSubmit={submitPost}>
              <input
                className="input-premium h-12 sm:h-14 px-4 text-base w-full"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Write a short title"
              />

              <textarea
                className="input-premium min-h-[130px] px-4 py-4 text-base resize-y w-full"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your update in a few lines..."
              />

              {/* ── Image slots ── */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-ink-soft">
                    Photos
                    <span className="ml-1.5 text-xs text-ink-muted font-normal">
                      ({slots.length}/{MAX_SLOTS})
                    </span>
                  </span>
                  {slots.length > 0 && (
                    <button type="button" onClick={clearAll} className="text-xs text-red-500 hover:text-red-700 transition">
                      Remove all
                    </button>
                  )}
                </div>

                {/* Slot list */}
                {slots.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {slots.map((slot, idx) => (
                      <div key={idx} className="flex items-center gap-3 rounded-2xl border border-black/8 bg-cream-deep/60 p-2.5">
                        {/* Thumbnail */}
                        {slot.type === "file" && slot.preview ? (
                          <img src={slot.preview} alt="" className="w-11 h-11 rounded-xl object-cover shrink-0 border border-black/8" />
                        ) : slot.type === "url" && slot.url ? (
                          <img
                            src={slot.url.includes("drive.google.com/file")
                              ? slot.url.replace(/drive\.google\.com\/file\/d\/([^/?#]+).*/, "drive.google.com/thumbnail?id=$1&sz=w80")
                              : slot.url}
                            alt=""
                            className="w-11 h-11 rounded-xl object-cover shrink-0 border border-black/8 bg-cream-deep"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-xl bg-cream-deep shrink-0 flex items-center justify-center">
                            <Camera className="w-4 h-4 text-ink-muted" />
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {slot.type === "file" ? (
                            <span className="text-sm text-ink-soft truncate block">{slot.file?.name}</span>
                          ) : (
                            <input
                              type="url"
                              className="input-premium h-9 text-sm px-3 w-full"
                              placeholder="Paste Google Drive or image URL..."
                              value={slot.url}
                              onChange={(e) => updateUrlSlot(idx, e.target.value)}
                            />
                          )}
                        </div>

                        <span className="text-xs text-ink-muted shrink-0">{idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeSlot(idx)}
                          className="shrink-0 rounded-full p-1 hover:bg-red-50 text-ink-muted hover:text-red-500 transition"
                          aria-label="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add buttons or empty state */}
                {slots.length < MAX_SLOTS ? (
                  <div className="flex flex-wrap gap-2">
                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border-2 border-dashed border-black/12 bg-cream-deep/50 hover:bg-cream-deep hover:border-brand/30 transition px-4 py-2 text-sm text-ink-muted hover:text-brand">
                      <Camera className="w-4 h-4" />
                      {slots.length === 0 ? "Add photos from device" : "Add more"}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFileSlots(e.target.files); e.target.value = ""; }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={addUrlSlot}
                      className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-black/12 bg-cream-deep/50 hover:bg-cream-deep hover:border-brand/30 transition px-4 py-2 text-sm text-ink-muted hover:text-brand"
                    >
                      <Link2 className="w-4 h-4" />
                      Add Drive / web link
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-ink-muted">Maximum {MAX_SLOTS} images reached.</p>
                )}
                <p className="mt-1.5 text-xs text-ink-muted">
                  JPEG / PNG / WEBP · Max 5 MB each · Google Drive links auto-convert
                </p>
              </div>

              <button
                type="submit"
                className="btn-primary inline-flex w-full sm:w-auto justify-center"
                disabled={submitting}
              >
                {submitting ? "Posting..." : "Post to feed"}
                <Send className="h-4 w-4" />
              </button>
            </form>
          ) : (
            <div className="mt-6 rounded-2xl bg-cream-deep/70 p-5">
              <p className="text-ink-soft leading-relaxed">
                Sign in as a member to share your own post on the public feed.
              </p>
              <Link to="/member/login" className="btn-secondary mt-4 inline-flex">
                Member sign-in
              </Link>
            </div>
          )}
        </div>

        {/* ── Feed ── */}
        <div className="space-y-6">
          {loadingPosts ? (
            <div className="card-premium text-center text-ink-muted">Loading feed...</div>
          ) : posts.length === 0 ? (
            <div className="card-premium text-center">
              <div className="font-heading text-2xl text-ink">No posts yet</div>
              <p className="mt-2 text-ink-soft">Be the first member to share a note or photo update.</p>
            </div>
          ) : (
            posts.map((post, index) => (
              <article key={post.id} className="card-premium overflow-hidden" data-testid={`feed-post-${index}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 rounded-full bg-cream-deep px-3 py-1 text-xs font-medium text-brand-secondary">
                      <Users2 className="h-3.5 w-3.5" />
                      {post.tag || "Member post"}
                    </div>
                    <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-light text-ink break-words">
                      {post.title}
                    </h2>
                    {post.meta ? <p className="mt-2 text-sm text-ink-muted">{post.meta}</p> : null}
                    <p className="mt-2 text-sm text-ink-muted">
                      By {post.member_name}
                      {post.member_membership_number ? ` · ${post.member_membership_number}` : ""}
                    </p>
                  </div>
                </div>

                <p className="text-ink-soft leading-relaxed max-w-3xl whitespace-pre-wrap break-words">
                  {post.body}
                </p>

                {/* Gallery (multi-photo — member or admin post) */}
                {post.gallery_srcs?.length > 0 && !failedImages[post.id] && (
                  <AutoImageSlider
                    images={post.gallery_srcs}
                    alt={post.title}
                    className="mt-6 h-64 sm:h-80 md:h-96 w-full rounded-2xl"
                    onImageError={() => setFailedImages((prev) => ({ ...prev, [post.id]: true }))}
                  />
                )}

                {/* Single image (legacy / no gallery) */}
                {!post.gallery_srcs?.length && post.has_image && !failedImages[post.id] && (
                  <AutoImageSlider
                    images={[feedImageUrl(post)]}
                    alt={post.title}
                    className="mt-6 h-64 sm:h-80 md:h-96 w-full rounded-2xl"
                    onImageError={() => setFailedImages((prev) => ({ ...prev, [post.id]: true }))}
                  />
                )}

                {failedImages[post.id] && (
                  <div className="mt-6 rounded-2xl border border-dashed border-black/10 bg-cream-deep/60 p-5 text-sm text-ink-muted">
                    This older post does not have a live image attached anymore.
                    The text is still saved, but the photo will need to be re-uploaded.
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </div>
    </ActivitiesShell>
  );
}
